import { useCallback, useSyncExternalStore } from 'react';
import {
  getConfigurationSectionOpenStateForProject,
  LSKey,
  notifyLocalStoreKeyUpdated,
  setConfigurationSectionOpenStateForProject,
  subscribeToLocalStoreKey,
} from '../store/localStore';
import { useSelectedProjectShortName } from './useSelectedProjectShortName';

/**
 * Persists and reads ConfigurationSection open/closed state per project and section id.
 * localStorage is the source of truth, while useSyncExternalStore keeps the component synchronized
 * with both same-tab writes (manual notify) and cross-tab writes (storage event).
 */
export function useConfigurationSectionOpenState(
  sectionId: string,
  defaultOpenState = true,
): { contentDisplayed: boolean; toggleOpenHandler: () => void } {
  const projectShortName = useSelectedProjectShortName();

  const getSnapshot = useCallback((): boolean => {
    return (
      getConfigurationSectionOpenStateForProject(projectShortName, sectionId) ?? defaultOpenState
    );
  }, [defaultOpenState, projectShortName, sectionId]);

  const subscribe = useCallback(
    (listener: () => void) =>
      subscribeToLocalStoreKey(LSKey.CONFIGURATION_SECTION_OPEN_STATE, listener),
    [],
  );
  const contentDisplayed = useSyncExternalStore(subscribe, getSnapshot, () => defaultOpenState);

  const toggleOpenHandler = useCallback(() => {
    setConfigurationSectionOpenStateForProject(projectShortName, sectionId, !contentDisplayed);
    notifyLocalStoreKeyUpdated(LSKey.CONFIGURATION_SECTION_OPEN_STATE);
  }, [contentDisplayed, projectShortName, sectionId]);

  return { contentDisplayed, toggleOpenHandler };
}
